import React from "react";
import { Box, Button, CircularProgress, Dialog, Divider, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PartStatusList from "./PartStatusList";

const NAVY = "#1E3A5F";
const FONT = "Inter, Poppins, sans-serif";
const GREEN = { main: "#079455", dark: "#067647", soft: "#ECFDF3", border: "#ABEFC6" };
const AMBER = { main: "#B54708", soft: "#FFFAEB", border: "#FEDF89" };

const card = { p: 1.75, borderRadius: "12px", border: "1px solid #EAECF0", backgroundColor: "#F9FAFB" };
const label = { fontSize: 12, fontWeight: 700, color: "#344054", mb: 0.75, letterSpacing: "0.01em", fontFamily: FONT };

/**
 * "Retry this policy?" / "Submit this policy again?" - the confirmation before a policy goes back to the automation
 * (2026-10-22: the new dialog look - icon and title, close button, soft cards, an icon on the warning, and buttons that
 * keep their text on one line). What it says and does is unchanged; OnlinePolicy.js works the plan out and passes it in.
 *
 * @param target        the row being retried ({ name, policyRef, attempts, maxAttempts }) or null (closed)
 * @param planView      { rerun, kept } for a policy with parts, or null (the old one-job wording)
 * @param statusText    the current status in words
 * @param resubmitPaid  a COMPLETED policy that is re-submittable only because it is paid by link and not paid yet
 * @param buttonText    "Yes, re-submit OD + TP" ...
 */
const RetryPolicyDialog = ({ target, planView, statusText, resubmitPaid, buttonText, submitting, onCancel, onConfirm }) => {
  const tone = resubmitPaid ? AMBER : GREEN;
  const close = () => !submitting && onCancel();

  return (
    <Dialog
      open={!!target}
      onClose={close}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px", fontFamily: FONT } }}
      data-testid="retry-policy-dialog"
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, px: 3, pt: 2.75, pb: 2, pr: 6, position: "relative" }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "12px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: tone.soft,
            border: `1px solid ${tone.border}`,
          }}
        >
          <ReplayRoundedIcon sx={{ color: tone.main }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" sx={{ fontWeight: 700, fontSize: 18, color: "#101828", lineHeight: 1.3, fontFamily: FONT }}>
            {resubmitPaid ? "Submit this policy again?" : "Retry this policy?"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#667085", mt: 0.25, fontFamily: FONT, wordBreak: "break-word" }}>
            {target?.name}
            {target?.policyRef ? ` · ${target.policyRef}` : ""}
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={close} disabled={submitting} sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ px: 3, pb: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {planView ? (
          // OD / TP / PA are separate: only the unfinished ones run again.
          <Box sx={card}>
            <Typography sx={label}>Will re-run</Typography>
            <PartStatusList parts={planView.rerun} maxWidth={360} />
            {planView.rerun
              .filter((p) => p.stopped)
              .map((p) => (
                <Typography key={p.key} sx={{ fontSize: 11.5, color: "#E04F16", mt: 0.5 }}>
                  {p.label}: stops again until its form is built
                </Typography>
              ))}

            {planView.kept.length > 0 && (
              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px dashed #D0D5DD" }}>
                <Typography sx={{ ...label, display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Inventory2OutlinedIcon sx={{ fontSize: 15, color: "#667085" }} />
                  Kept — already bought (never bought twice)
                </Typography>
                <PartStatusList parts={planView.kept} maxWidth={360} />
              </Box>
            )}

            <Typography sx={{ fontSize: 12, color: "#667085", mt: 1.25, lineHeight: 1.55, fontFamily: FONT }}>
              Current status <strong style={{ color: "#344054" }}>{statusText}</strong>
              {" · "}attempts start again for the parts above{" · "}error history is kept
            </Typography>
          </Box>
        ) : (
          <Box sx={card}>
            <Typography sx={{ fontSize: 13.5, color: "#344054", lineHeight: 1.55, fontFamily: FONT }}>
              The current queue entry will be <strong>deleted</strong> and a new one created from the policy as it stands
              now. The automation starts it again within a few seconds.
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#667085", mt: 1, lineHeight: 1.55, fontFamily: FONT }}>
              Current status <strong style={{ color: "#344054" }}>{statusText}</strong>
              {" · "}attempts {target?.attempts}/{target?.maxAttempts}
              {" · "}error history is kept
            </Typography>
          </Box>
        )}

        {/* The one case that is not simply "try the failed thing again". */}
        {resubmitPaid && (
          <Box
            role="alert"
            sx={{
              display: "flex",
              gap: 1.25,
              alignItems: "flex-start",
              p: 1.5,
              borderRadius: "12px",
              border: `1px solid ${AMBER.border}`,
              borderLeft: `4px solid ${AMBER.main}`,
              backgroundColor: AMBER.soft,
            }}
          >
            <WarningAmberRoundedIcon sx={{ color: AMBER.main, fontSize: 20, mt: "1px" }} />
            <Typography sx={{ fontSize: 13, color: AMBER.main, lineHeight: 1.55, fontFamily: FONT }}>
              This policy has already <strong>completed</strong> at the insurer. It is only re-submittable because it is
              paid by payment link and the customer has not paid yet — check that before continuing.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Actions */}
      <Divider />
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          gap: 1.25,
          justifyContent: "flex-end",
          flexDirection: { xs: "column-reverse", sm: "row" },
        }}
      >
        <Button
          onClick={close}
          disabled={submitting}
          sx={{ textTransform: "none", fontWeight: 600, color: "#344054", borderRadius: "10px", px: 2.5, fontFamily: FONT }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={submitting}
          onClick={onConfirm}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <ReplayRoundedIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            whiteSpace: "nowrap",
            borderRadius: "10px",
            px: 2.5,
            fontFamily: FONT,
            backgroundColor: resubmitPaid ? NAVY : GREEN.main,
            "&:hover": { backgroundColor: resubmitPaid ? "#16304F" : GREEN.dark },
          }}
        >
          {submitting ? "Submitting…" : buttonText}
        </Button>
      </Box>
    </Dialog>
  );
};

export default RetryPolicyDialog;
